import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import router from '@/router';
import { uuid } from 'vue-uuid';
import Cookies from 'js-cookie';
import _ from 'lodash';

/** API_URL env var */
declare const API_URL: string;

/** IDP_API_BASE_URL: valeur du cookie ep-origin */
const IDP_API_BASE_URL: string | undefined = Cookies.get('ep-origin') ? Cookies.get('ep-origin') : '';

const CSRF_TOKEN: string = 'CSRF-TOKEN';
const X_CSRF_TOKEN: string = 'X-CSRF-TOKEN';
const CSRF_TOKEN_VALIDITY: number = 13600000;

const enum HttpStatusCode {
	INTERNAL_SERVER_ERROR = 500,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	NOT_FOUND = 404,
	SERVICE_UNAVAILABLE = 503
}

const config = {
	withCredentials: true,
	baseURL: API_URL ? API_URL : '',
	headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/json',
		'sessionID': uuid.v4()
	}
};

// config pour la requête deconnexionPatient à IDPA-API
const configIDP = {
	withCredentials: true,
	baseURL: IDP_API_BASE_URL,
	headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/json',
		'sessionID': uuid.v4()
	}
};

export interface CommonResponse {
	libelleError: string;
	codeError: string;
	subcodeError: string;
	codeTrace: string;
}

const handleCsrfCookie = () => {
	if (!Cookies.get(CSRF_TOKEN)) {
		Cookies.set(CSRF_TOKEN, uuid.v4(), {
			// 1h de validité
			expires: new Date(Date.now() + CSRF_TOKEN_VALIDITY)
		});
	}
};
handleCsrfCookie();

const confInterceptor = (config: AxiosRequestConfig) => {
	if (config && config.headers) {
		config.headers.correlationID = uuid.v4();
		const csrfCookie = Cookies.get(CSRF_TOKEN);
		if (csrfCookie) {
			config.headers[X_CSRF_TOKEN] = csrfCookie;
		}
	}

	return config;
};

/** The axios instance */
const instance = axios.create(config);
instance.interceptors.request.use(confInterceptor);

/** The axios instance pour deconnexionPatient */
const instanceIDP = axios.create(configIDP);
instanceIDP.interceptors.request.use(confInterceptor);

/** The axios instance with no interceptors */
const instanceNoInterceptors = axios.create(config);
instanceNoInterceptors.interceptors.request.use(confInterceptor);

const responseInterceptor = ((response: AxiosResponse) => {
	if (!_.isNil(_.get(response, 'headers["action-forbidden"]'))) {
		router.push('/error/500');
		return Promise.reject(response);
	}

	return Promise.resolve(response);
});

const errorInterceptor = ((error: AxiosError) => {
	if (error.config.manualErrorHandling) {
		return Promise.reject(error);
	}

	const erreur500 = 'erreur500';
	if (typeof error.response === 'undefined') {
		router.push({ name: erreur500 });
		return Promise.reject(error);
	}

	const status = error.response.status;

	let toPush;
	if (status === HttpStatusCode.UNAUTHORIZED.valueOf()) {
		if (error.response.data.message === 'expire') {
			toPush = 'erreur401Expire';
		} else {
			toPush = 'erreur401';
		}
	} else if (status === HttpStatusCode.FORBIDDEN.valueOf()) {
		toPush = 'erreur403';
	} else if (status === HttpStatusCode.NOT_FOUND.valueOf()) {
		toPush = 'erreur404';
	} else if (status === HttpStatusCode.SERVICE_UNAVAILABLE.valueOf()) {
		toPush = 'erreur503';
	} else {
		toPush = undefined;
	}

	if (toPush) {
		router.push({ name: toPush, params: { error: _.get(error, 'response.data.erreur', '') } });
	}

	return Promise.reject(error);
});

instance.interceptors.response.use(responseInterceptor, errorInterceptor);

export { instance as axios };
export { instanceIDP as axiosIDP };
export { instanceNoInterceptors as axiosNoInterceptors };
export { AxiosResponse };
